"use client";

import { motion } from 'framer-motion';
import LambLogo from '@/components/lamb-logo';
import { useLanguage } from '@/contexts/language-context';
import { useEffect, useState } from 'react';

const LoadingScreen = () => {
  const { t } = useLanguage();
  const [particles, setParticles] = useState<any[]>([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Only run on client
    setParticles(
      Array.from({ length: 8 }).map(() => ({
        left: Math.random() * 100,
        top: Math.random() * 100,
        randomPath: {
          x: [0, (Math.random() - 0.5) * 120, (Math.random() - 0.5) * 100, (Math.random() - 0.5) * 80, 0],
          y: [0, -15 - Math.random() * 30, 10 + Math.random() * 20, -10 - Math.random() * 15, 0],
          scale: [1, 0.7 + Math.random() * 0.6, 0.5 + Math.random() * 1, 0.8 + Math.random() * 0.4, 1],
          rotate: [0, (Math.random() - 0.5) * 45, (Math.random() - 0.5) * 60, (Math.random() - 0.5) * 30, 0],
          opacity: [0.3, 0.8, 0.4, 0.9, 0.3]
        },
        colorClass: (() => {
          const i = Math.floor(Math.random() * 3);
          return i === 0 ? 'bg-cloud-blue/40' : i === 1 ? 'bg-cloud-lavender/40' : 'bg-cream/30';
        })(),
        duration: 6 + Math.random() * 8,
        delay: Math.random() * 5,
      }))
    );
    setMounted(true);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.8, ease: "easeInOut" }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-deep-purple via-deep-purple/90 to-deep-purple/80"
    >
      {/* Animated background blobs */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-20 -right-20 w-96 h-96 blob blob-1 opacity-10"
          animate={{
            x: [0, 80, -40, 60, 0],
            y: [0, -40, 30, -20, 0],
            scale: [1, 1.2, 0.8, 1.1, 1],
            rotate: [0, 20, -15, 25, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
            times: [0, 0.25, 0.5, 0.75, 1],
          }}
        />

        <motion.div
          className="absolute -bottom-20 -left-20 w-80 h-80 blob blob-2 opacity-15"
          animate={{
            x: [0, -60, 40, -30, 0],
            y: [0, 50, -30, 40, 0],
            scale: [1, 0.7, 1.3, 0.9, 1],
            rotate: [0, -25, 30, -10, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 5,
            times: [0, 0.3, 0.6, 0.8, 1],
          }}
        />

        {/* Floating particles with random flowing movement (client only) */}
        {mounted && particles.map((particle, i) => (
          <motion.div
            key={i}
            className={`absolute w-3 h-3 rounded-full ${particle.colorClass}`}
            style={{ left: `${particle.left}%`, top: `${particle.top}%` }}
            animate={particle.randomPath}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              ease: "easeInOut",
              delay: particle.delay,
              times: [0, 0.25, 0.5, 0.75, 1],
            }}
          />
        ))}
      </div>

      {/* Main loading content */}
      <div className="relative z-10 text-center">
        {/* Logo with enhanced animation */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{
            type: "spring",
            stiffness: 200,
            damping: 20,
            duration: 1.5,
          }}
          className="mb-8"
        >
          <LambLogo className="w-32 h-32 md:w-40 md:h-40 mx-auto" />
        </motion.div>

        {/* Company name with staggered animation */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mb-6"
        >
          <h1 className="text-4xl md:text-5xl font-light text-cream mb-2">
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              Agnus
            </motion.span>{' '}
            <motion.span
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1.0 }}
              className="bg-gradient-to-r from-cloud-blue to-cloud-lavender bg-clip-text text-transparent"
            >
              Cloud
            </motion.span>
          </h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.2 }}
            className="text-lg text-cream/70 font-light"
          >
            {t('loading.tagline')}
          </motion.p>
        </motion.div>

        {/* Loading animation */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.4 }}
          className="flex flex-col items-center"
        >
          {/* Progress bar */}
          <div className="w-64 h-1 bg-cloud-lavender/30 rounded-full overflow-hidden mb-4">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ duration: 2.5, delay: 1.6, ease: "easeInOut" }}
              className="h-full bg-gradient-to-r from-cloud-blue to-cloud-lavender rounded-full"
            />
          </div>

          {/* Loading dots */}
          <div className="flex space-x-2">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 bg-cloud-blue rounded-full"
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2 + 1.8,
                  ease: "easeInOut",
                }}
              />
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 2.0 }}
            className="text-sm text-cream/60 mt-4 font-light"
          >
            {t('loading.preparing')}
          </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LoadingScreen;